import * as React from 'react';
import type {
  ImpoundGroupBy,
  ImpoundStatusFilter,
  ImpoundResponseDTO,
  ImpoundSortBy,
} from '@relay-towing/api';
import {
  Button,
  impoundStatus,
  ImpoundCard,
  type ListMode,
  ListToolbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@relay-towing/ui';
import { Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useMember } from '../../hooks/useMember';
import { usePagedList } from '../../hooks/usePagedList';
import { PagedRows } from '../shell/PagedRows';
import { SortFilters } from '../shell/SortFilters';
import { ScanQrButton } from '../shell/ScanQrButton';
import { FunctionPage } from '../functions/FunctionPage';
import { vehicleOf } from '../vehicle';
import { NewImpoundSheet } from './NewImpoundSheet';

type Sort = Extract<ImpoundSortBy, 'newest' | 'oldest'>;

const SORTS: Array<{ value: Sort; label: string }> = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

type View = 'open' | 'closed';

/**
 * Open is what is still at the lot; Closed is what has left it, however it
 * went, banded Released, Junked and Lien-sold. A quick toggle over the list,
 * as Invoices has Current and Archived.
 */
const VIEW_STATUSES: Record<View, ImpoundStatusFilter[]> = {
  open: ['ACTIVE'],
  closed: ['RELEASED', 'JUNKED', 'SOLD'],
};

const days = (count: number) => `${count} ${count === 1 ? 'day' : 'days'}`;

/**
 * The impounds tab: the dashboard's grouped list (Figma 45-5523), a band for
 * At lot, Released, Junked and Lien-sold, searched by plate, VIN or number
 * and sorted on the server. Whose impounds they are is the server's answer:
 * the impounds from a driver's calls (`impounds.view_own`) or the
 * company's. The server groups them (`groupBy=status`) and counts each band
 * across every page.
 *
 * A member who only drives is not sent an impound's status, days held or
 * anything past the vehicle and where it is. The bands still say which is
 * which, because the server orders the rows by status and counts each
 * group, as it filtered by status for the tabs this list replaced; the rows
 * simply carry no days, and the table no Status or Days held.
 */
export function ImpoundsScreen() {
  const navigate = useNavigate();
  const member = useMember();
  const [creating, setCreating] = React.useState(false);
  const creates = !!member.officeRole && member.can('impounds.create');
  const [sort, setSort] = React.useState<Sort>('newest');
  const [mode, setMode] = React.useState<ListMode>('list');
  const [search, setSearch] = React.useState('');
  const searchQuery = useDebouncedValue(search).trim() || undefined;
  const [view, setView] = React.useState<View>('open');
  const list = usePagedList<ImpoundResponseDTO>(
    'mobile-impounds',
    '/impounds',
    {
      groupBy: 'status' satisfies ImpoundGroupBy,
      status: VIEW_STATUSES[view],
      sortBy: sort,
      searchQuery,
    }
  );
  // Only what the server sent: a member who only drives gets no status.
  const office = list.items.some((impound) => impound.status);
  const open = (impound: ImpoundResponseDTO) =>
    navigate(`/impounds/${impound.id}`);

  return (
    <FunctionPage
      id="impounds"
      title="Impounds"
      actions={
        // The office brings a vehicle in from any call; someone who only
        // drives does it from their own, as they complete it.
        creates ? (
          <Button
            variant="soft"
            size="lg"
            pill
            leadingIcon={<Plus />}
            onClick={() => setCreating(true)}
          >
            New
          </Button>
        ) : undefined
      }
    >
      {creates ? (
        <NewImpoundSheet open={creating} onOpenChange={setCreating} />
      ) : null}
      <Tabs value={view} onValueChange={(next) => setView(next as View)}>
        <TabsList fullWidth>
          <TabsTrigger value="open" fullWidth>
            Open
          </TabsTrigger>
          <TabsTrigger value="closed" fullWidth>
            Closed
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <PagedRows
        list={list}
        empty={searchQuery ? 'No impounds match' : 'No impounds yet'}
        groups={{
          of: (impound) => impound.status || undefined,
          label: (status) => impoundStatus(status).label,
          tone: (status) => impoundStatus(status).tone,
        }}
        toolbar={
          <ListToolbar
            mode={mode}
            onModeChange={setMode}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Plate, VIN or number"
            searchLabel="Search impounds"
          >
            <SortFilters options={SORTS} value={sort} onValueChange={setSort} />
            {/* The scanner reads the tag on a vehicle at the lot; the code
                goes to the server's search, which matches the impound
                number, rather than to a match against the rows loaded so
                far, as the old scanner did. */}
            <ScanQrButton
              label="Scan an impound's QR code"
              hint="Point the camera at the QR tag on the vehicle"
              onCode={setSearch}
            />
          </ListToolbar>
        }
        table={
          mode === 'table'
            ? (impounds) => (
                <Table shape="band">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Number</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Plate</TableHead>
                      {office ? <TableHead>Status</TableHead> : null}
                      {office ? <TableHead>Days held</TableHead> : null}
                      <TableHead>Lot</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {impounds.map((impound) => {
                      const status = impound.status
                        ? impoundStatus(impound.status)
                        : undefined;
                      return (
                        <TableRow
                          key={impound.id}
                          interactive
                          onClick={() => open(impound)}
                        >
                          <TableCell>{impound.impoundNumber}</TableCell>
                          <TableCell>
                            {vehicleOf(impound.dispatch?.vehicle) || '—'}
                          </TableCell>
                          <TableCell>
                            {impound.dispatch?.vehicle?.licensePlate || '—'}
                          </TableCell>
                          {office ? (
                            <TableCell>
                              {/* Words, as the design's table has them: the
                                  bands in the list are where status is a
                                  colour. */}
                              {status?.label ?? '—'}
                            </TableCell>
                          ) : null}
                          {office ? (
                            <TableCell>
                              {impound.daysHeld !== undefined &&
                              impound.daysHeld !== null
                                ? days(impound.daysHeld)
                                : '—'}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {impound.location?.name ||
                              impound.storageLot ||
                              '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )
            : undefined
        }
      >
        {(impound) => (
          <ImpoundCard
            key={impound.id}
            platform="phone"
            onClick={() => open(impound)}
            number={impound.impoundNumber}
            color={impound.dispatch?.vehicle?.vehicleAttributes?.color}
            vehicle={
              vehicleOf(impound.dispatch?.vehicle) || 'Unspecified vehicle'
            }
            lot={impound.location?.name || impound.storageLot}
            // Someone who only drives is not sent how long it has been held,
            // and the card reads well without.
            heldFor={
              impound.daysHeld !== undefined && impound.daysHeld !== null
                ? days(impound.daysHeld)
                : undefined
            }
            plate={impound.dispatch?.vehicle?.licensePlate}
          />
        )}
      </PagedRows>
    </FunctionPage>
  );
}
